import { createSelector } from 'reselect';

import { isBulkMediaViewLocation } from '../../helpers/bulkMediaHash';
import { getActiveProduct } from '../products/selectors';
import {
  getProductCheckboxState,
  getVariantCheckboxState,
  isBulkHighlighted,
} from '../products/helpers/treeSelection';
import { isMasterSelectionContext } from '../products/helpers/selectionContext';
import { buildSelectionKey } from '../products/helpers/selectionKey';
import resolveDetailsPanelProduct from '../products/helpers/resolveDetailsPanelProduct';

export const selectProductSelection = (state) => state.products.ui.selection;

export const selectIsBulkMediaView = (state) => isBulkMediaViewLocation({
  pathname: state.router?.location?.pathname,
  hash: state.router?.location?.hash,
  selectionCount: state.products?.ui?.selection?.keys?.length || 0,
  selectionSource: state.products?.ui?.selection?.source,
});

export const selectProductSelectionFeedback = (state) => (
  state.products.ui.selectionFeedback
);

export const selectProductSelectionContext = createSelector(
  selectProductSelection,
  (selection) => selection.context,
);

export const selectProductSelectionSource = createSelector(
  selectProductSelection,
  (selection) => selection.source,
);

export const selectProductSelectionCount = createSelector(
  selectProductSelection,
  (selection) => selection.keys.length,
);

export const selectDetailsPanelProduct = createSelector(
  selectProductSelection,
  (state) => state.products?.search?.items || [],
  (state) => state.router?.location,
  (selection, searchItems, location) => resolveDetailsPanelProduct({
    selection,
    searchItems,
    activeProduct: getActiveProduct(location),
  }),
);

export const selectProductSelectionStats = createSelector(
  selectProductSelection,
  (selection) => {
    let products = 0;
    let variants = 0;
    selection.keys.forEach((key) => {
      const type = selection.byKey[key]?.type;
      if (type === 'masterVariant' || type === 'listingVariant') variants += 1;
      else products += 1;
    });
    return { products, variants, total: selection.keys.length };
  },
);

// [{ type, id }] — the exact wire shape products.exportCSV's `recordIds`-adjacent selection
// payload needs; deliberately never resolved into parents/children.
export const selectProductSelectionPayload = createSelector(
  selectProductSelection,
  (selection) => selection.keys.map((key) => ({
    type: selection.byKey[key].type,
    id: selection.byKey[key]._id,
  })),
);

export const selectIsProductSelected = (state, key) => {
  const selection = state.products.ui.selection;
  return Boolean(selection.byKey[key]) && selection.source !== 'tree';
};

export const selectIsTreeBulkHighlighted = (state, isVariant, nodeId) => {
  const selection = selectProductSelection(state);
  if (selection.source !== 'tree') return false;
  if (!isMasterSelectionContext(selection.context)) return false;
  return isBulkHighlighted(isVariant, nodeId, selection);
};

export const selectTreeProductCheckboxState = (state, productId, variantIds) => {
  const selection = selectProductSelection(state);
  if (selection.source !== 'tree') return false;
  if (!isMasterSelectionContext(selection.context) && selection.keys.length) {
    return false;
  }
  return getProductCheckboxState(productId, variantIds, selection);
};

export const selectTreeVariantCheckboxState = (state, variantId) => {
  const selection = selectProductSelection(state);
  if (selection.source !== 'tree') return false;
  if (!isMasterSelectionContext(selection.context) && selection.keys.length) {
    return false;
  }
  return getVariantCheckboxState(variantId, selection);
};

export const selectTreeSelectionKey = (isVariant, nodeId) => (
  buildSelectionKey({ type: isVariant ? 'masterVariant' : 'master', _id: nodeId })
);
